// SPDX-License-Identifier: MIT
import { supabase } from '../auth/supabase';

// Derive the clone-related URLs + the bare repo name from the Forgejo
// html_url (the only URL we persist on public.projects.forgejo_repo_url).
// Pattern is stable across the Forgejo install on git.myconciergerie.fr :
//   html_url   = https://git.myconciergerie.fr/<owner>/<repo>
//   clone_url  = <html_url>.git
//   ssh_url    = ssh://git@git.myconciergerie.fr:222/<owner>/<repo>.git
//   repo_name  = <repo>
// Returns empty strings on a malformed URL — the UI then falls back to
// showing the raw html_url as a link.
export interface DerivedForgejoUrls {
  html_url: string;
  clone_url: string;
  ssh_url: string;
  repo_name: string;
  owner: string;
}

// v23 2026-05-17 — Magic double-click installers, generated client-side.
// The file content embeds the GENESIS_REPO env var so the downloaded
// script knows what to clone without prompting. User saves the file,
// double-clicks it from Explorer / Finder, install runs.
//
// Trust model : the .bat / .command itself ONLY exports the env var
// and pipes the same hosted install.{ps1,sh} as the one-liner. Body
// is trivial. Operator audits by opening the downloaded file (Notepad
// for .bat, Text Editor for .command).
//
// Windows note : double-click on a .bat triggers SmartScreen "Windows
// protected your PC" because it's unsigned. User clicks "More info"
// → "Run anyway" the first time.
//
// Mac note : double-click on .command requires the file to be marked
// executable. Browsers download with mode 644, so the first run needs
// right-click → Open → "Open anyway" (Gatekeeper). The .command is
// honestly a band-aid on Mac since Spotlight + Terminal is the
// muscle-memory path there.

export interface MagicInstallerOptions {
  htmlUrl: string;
  os: 'windows' | 'mac';
}

export function buildMagicInstallerContent(opts: MagicInstallerOptions): string {
  if (opts.os === 'windows') {
    return `@echo off
REM Genesis magic installer for Windows
REM Generated by https://genesis.myconciergerie.fr/ for the project below.
REM Double-click this file from Explorer to run.
REM What it does : opens PowerShell + downloads + runs the install script
REM that installs Node.js (if missing), Claude Code, clones your repo,
REM and launches Claude on Action 1 of your FIRST_SESSION brief.
REM
REM Audit : https://genesis.myconciergerie.fr/install/install.ps1

echo.
echo === Genesis magic installer ===
echo Target repo : ${opts.htmlUrl}
echo.
echo About to launch PowerShell. If Windows SmartScreen warns you
echo about this .bat, click "More info" then "Run anyway".
echo Press Ctrl+C in the next 5 seconds to cancel.
timeout /t 5 /nobreak >nul 2>&1

powershell -ExecutionPolicy Bypass -NoExit -Command "$env:GENESIS_REPO='${opts.htmlUrl}'; irm https://genesis.myconciergerie.fr/install/install.ps1 | iex"

pause
`;
  }
  // Mac .command
  return `#!/bin/bash
# Genesis magic installer for macOS
# Generated by https://genesis.myconciergerie.fr/ for the project below.
# Double-click this file from Finder to run.
#
# First-time Gatekeeper note : Mac blocks unsigned .command files. If
# double-click doesn't work, right-click this file → Open → "Open"
# in the confirmation dialog. You only do this dance once per file.
#
# Audit : https://genesis.myconciergerie.fr/install/install.sh

echo ""
echo "=== Genesis magic installer ==="
echo "Target repo : ${opts.htmlUrl}"
echo ""
sleep 1

export GENESIS_REPO='${opts.htmlUrl}'
bash -c "$(curl -fsSL https://genesis.myconciergerie.fr/install/install.sh)"

echo ""
echo "Press Enter to close this window."
read -r _
`;
}

// Trigger a browser download of the magic installer for the given OS.
// Uses an anchor-click pattern (no popup, no permission prompt, just
// a download to the user's Downloads folder).
export function downloadMagicInstaller(htmlUrl: string, os: 'windows' | 'mac'): void {
  const rawContent = buildMagicInstallerContent({ htmlUrl, os });
  // v23a 2026-05-17 — Line-ending fix : Windows cmd.exe is sensitive
  // to LF-only line endings in .bat files (some commands like `pause`
  // mis-parse). Force CRLF on Windows ; Mac .command bash accepts LF
  // fine. Also prepend a BOM-free UTF-8 — most cmd.exe versions accept
  // UTF-8 without BOM, and a BOM with `@echo off` confuses cmd's first
  // line parse.
  const content = os === 'windows'
    ? rawContent.replace(/\r?\n/g, '\r\n')
    : rawContent;
  const ext = os === 'windows' ? 'bat' : 'command';
  const mime = os === 'windows' ? 'application/x-bat' : 'application/x-sh';
  // Repo slug-derived filename so the user knows what project this
  // installer is for (helpful when they have multiple .bat files in
  // Downloads).
  const filename = `genesis-install-${deriveForgejoUrls(htmlUrl).repo_name || 'project'}.${ext}`;
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

export function deriveForgejoUrls(htmlUrl: string): DerivedForgejoUrls {
  try {
    const u = new URL(htmlUrl);
    const segs = u.pathname.replace(/^\/+|\/+$/g, '').split('/');
    if (segs.length < 2) {
      return { html_url: htmlUrl, clone_url: '', ssh_url: '', repo_name: '', owner: '' };
    }
    const owner = segs[0];
    const repo = segs[1].replace(/\.git$/, '');
    const clone_url = `${u.origin}/${owner}/${repo}.git`;
    const ssh_url = `ssh://git@${u.host}:222/${owner}/${repo}.git`;
    return { html_url: htmlUrl, clone_url, ssh_url, repo_name: repo, owner };
  } catch {
    return { html_url: htmlUrl, clone_url: '', ssh_url: '', repo_name: '', owner: '' };
  }
}

// Result of one POST /forgejo-bootstrap call. Mirrors the edge fn's
// `ForgejoBootstrapResult` (supabase/functions/forgejo-bootstrap/lib.ts).
export interface ForgejoBootstrapResult {
  repo_url: string;
  html_url: string;
  clone_url: string;
  ssh_url: string;
  default_branch: string;
  already_exists: boolean;
  // Read-only token (FORGEJO_CLONE_TOKEN) so the clone command authenticates
  // against the login-walled instance — automatic for ALL users, zero SSH-key /
  // zero per-user setup. null when the instance hasn't configured the token.
  clone_token?: string | null;
}

// Build an authenticated HTTPS clone URL: the instance Forgejo is login-walled
// (REQUIRE_SIGNIN_VIEW) so anonymous clone fails and SSH needs a per-user key.
// Embedding a READ-ONLY token as basic-auth makes `git clone` work for everyone
// with no setup. Falls back to the plain clone URL when no token is available.
//   https://<owner>:<token>@git.myconciergerie.fr/<owner>/<repo>.git
export function buildAuthedCloneUrl(htmlUrl: string, token: string | null | undefined): string {
  const { clone_url, owner } = deriveForgejoUrls(htmlUrl);
  if (!token || !clone_url || !owner) return clone_url || htmlUrl;
  try {
    const u = new URL(clone_url);
    return `${u.protocol}//${encodeURIComponent(owner)}:${encodeURIComponent(token)}@${u.host}${u.pathname}`;
  } catch {
    return clone_url || htmlUrl;
  }
}

// Trigger the forgejo-bootstrap edge function for a saved project. The fn
// reads the project row via RLS, creates a new Forgejo repo on the platform
// account, pushes README.md + .gitignore + .claude/CLAUDE.md + manifest.json,
// then persists forgejo_repo_url on the row. Idempotent : a second call on
// the same project returns already_exists:true with the stored URL.
//
// Error contract — the edge fn returns a JSON {error: "<code>"} on every 4xx
// / 5xx ; we surface them as Error with .status attached for caller branching.
export async function bootstrapForgejoRepo(
  projectId: string,
  matiereText?: string,
): Promise<ForgejoBootstrapResult> {
  const { data, error } = await supabase.functions.invoke<ForgejoBootstrapResult>(
    'forgejo-bootstrap',
    { body: { project_id: projectId, ...(matiereText ? { matiere_text: matiereText } : {}) } },
  );
  if (error) {
    const ctx = (error as { context?: Response }).context;
    const status = ctx?.status;
    const wrapped = new Error(error.message) as Error & { status?: number };
    if (typeof status === 'number') wrapped.status = status;
    throw wrapped;
  }
  if (!data) throw new Error('Empty response from forgejo-bootstrap function');
  return data;
}
